import React, { useState, useEffect } from "react";
import { SignOutButton } from "@clerk/clerk-react";
import { getDomains } from "../service/api/domainService";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules"; // Import depuis swiper/modules
import "swiper/css";
import "swiper/css/navigation";
import { FaCopy, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { toast } from "sonner";

const NoDomainFound = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(null);
  const [hoveredEmail, setHoveredEmail] = useState(null);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const data = await getDomains();
        // Filtrer pour ne garder que les domaines ayant une photo et un email
        const validDomains = data.filter((domain) => domain.photo && domain.email);
        setDomains(validDomains);
      } catch (err) {
        setError(err.message || "Échec du chargement des domaines.");
      } finally {
        setLoading(false);
      }
    };

    fetchDomains();
  }, []);

  const handleCopy = (email) => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        setCopiedEmail(email);
        // Efface le message "copié" après 2 secondes
        setTimeout(() => setCopiedEmail(null), 2000);
      })
      .catch(() => {
        toast.error("Erreur lors de la copie", { position: "top-center" });
      });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <p className="text-muted">Chargement des domaines...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100 bg-light">
        <p className="text-danger">Erreur : {error}</p>
      </div>
    );
  }

  return (
    <div className="container-fluid bg-white" style={{ minHeight: "100vh" }}>
      <div className="d-flex flex-column align-items-center">
        {/* Div d'information centrée avec une grande marge en haut */}
        <div
          className="rounded shadow-sm p-4 text-center mb-5 mt-5 border border-warning bg-light"
          style={{ maxWidth: "500px", width: "100%" }}
        >
          <h4 className="mb-3 text-warning fw-bold">⚠️ Accès refusé</h4>
          <p className="mb-3 fs-6 text-dark">
            Votre compte Clerk a bien été détecté, mais nous n'avons trouvé aucun enregistrement
            correspondant dans notre base de données.
            <br />
            Veuillez contacter un administrateur ou l'un des domaines listés ci-dessous pour plus d’informations.
          </p>
          <SignOutButton>
            <button className="btn btn-warning mt-3 w-100 fw-bold">
              Se déconnecter
            </button>
          </SignOutButton>
        </div>

        {/* Swiper agrandi pour défiler horizontalement les images */}
        {domains.length > 0 ? (
          <div className="w-100" style={{ maxWidth: "800px" }}>
            <Swiper
              modules={[Navigation]}
              navigation={{
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
              }}
              spaceBetween={30}
              slidesPerView={3}
              breakpoints={{
                320: { slidesPerView: 1 },
                576: { slidesPerView: 2 },
                768: { slidesPerView: 3 },
              }}
            >
              {domains.map((domain, index) => (
                <SwiperSlide key={index}>
                  <div className="text-center">
                    <img
                      src={`${import.meta.env.VITE_API_BASE_URL}${domain.photo}`}
                      alt={`Domaine ${index + 1}`}
                      className="rounded-circle shadow-sm"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        border: "2px solid #dee2e6",
                        margin: "0 auto",
                      }}
                    />
                    {/* Zone de l'email avec gestion du survol */}
                    <div
                      className="position-relative mt-2"
                      style={{ display: "inline-block" }}
                      onMouseEnter={() => setHoveredEmail(domain.email)}
                      onMouseLeave={() => setHoveredEmail(null)}
                    >
                      <span className="text-muted small">{domain.email}</span>
                      {hoveredEmail === domain.email && (
                        <FaCopy
                          style={{ cursor: "pointer", marginLeft: "5px" }}
                          onClick={() => handleCopy(domain.email)}
                          title="Copier l'email"
                        />
                      )}
                      {copiedEmail === domain.email && (
                        <span className="text-success small ms-2">copié</span>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
              {/* Boutons de navigation personnalisés avec vraies icônes */}
              <div
                className="swiper-button-prev bg-white"
                style={{
                  color: "#000",
                  fontSize: "1.5rem",
                  top: "50%",
                  left: "0",
                  zIndex: "10",
                }}
              >
                <FaArrowLeft />
              </div>
              <div
                className="swiper-button-next bg-white"
                style={{
                  color: "#000",
                  fontSize: "1.5rem",
                  top: "50%",
                  right: "0",
                  zIndex: "10",
                }}
              >
                <FaArrowRight />
              </div>
            </Swiper>
          </div>
        ) : (
          <p className="text-muted text-center">Aucun domaine disponible pour le moment.</p>
        )}
      </div>
    </div>
  );
};

export default NoDomainFound;
